// ==================== TRANSFORMATION DATA ====================

export const INDUSTRY_TRANSFORMATION_DATA = {
  healthcare: {
    industryName: 'Healthcare',
    transformationFocus: ['Clinical Excellence', 'Patient Experience', 'Operational Efficiency', 'Research Innovation'],
    digitalMaturityBaseline: 2.5,
    targetMaturity: 4.5,
    valueMultipliers: {
      revenue: 0.18,
      cost: 0.22,
      margin: 0.04,
      asset: 0.28,
      productivity: 0.35,
      throughput: 0.25,
      workingCapital: 0.15
    },
    keyKPIs: [
      'Patient Satisfaction Score',
      'Clinical Outcome Improvement',
      'Operational Cost per Patient',
      'Time to Diagnosis',
      'Readmission Rate Reduction'
    ],
    transformationTimeline: '18-24 months',
    investmentRange: { low: 500000, high: 5000000 },
    expectedROI: '250-400%',
    priorityUseCases: [
      'AI-powered Diagnostics',
      'Predictive Patient Monitoring',
      'Personalized Treatment Plans',
      'Automated Administrative Tasks',
      'Drug Discovery Acceleration'
    ]
  },
  
  finance: {
    industryName: 'Financial Services',
    transformationFocus: ['Risk Management', 'Customer Intelligence', 'Operational Efficiency', 'Regulatory Compliance'],
    digitalMaturityBaseline: 3.0,
    targetMaturity: 4.8,
    valueMultipliers: {
      revenue: 0.15,
      cost: 0.25,
      margin: 0.05,
      asset: 0.20,
      productivity: 0.40,
      throughput: 0.30,
      workingCapital: 0.25
    },
    keyKPIs: [
      'Risk-adjusted Return',
      'Cost-to-Income Ratio',
      'Customer Lifetime Value',
      'Fraud Detection Rate',
      'Compliance Violation Reduction'
    ],
    transformationTimeline: '12-18 months',
    investmentRange: { low: 1000000, high: 10000000 },
    expectedROI: '300-500%',
    priorityUseCases: [
      'Real-time Fraud Detection',
      'Algorithmic Trading',
      'Credit Risk Scoring',
      'Anti-Money Laundering',
      'Customer Churn Prediction'
    ]
  },
  
  ecommerce: {
    industryName: 'E-commerce & Retail',
    transformationFocus: ['Customer Experience', 'Supply Chain Optimization', 'Dynamic Pricing', 'Inventory Management'],
    digitalMaturityBaseline: 3.2,
    targetMaturity: 4.6,
    valueMultipliers: {
      revenue: 0.20,
      cost: 0.18,
      margin: 0.03,
      asset: 0.22,
      productivity: 0.30,
      throughput: 0.28,
      workingCapital: 0.22
    },
    keyKPIs: [
      'Conversion Rate',
      'Average Order Value',
      'Customer Retention Rate',
      'Inventory Turnover',
      'Gross Margin'
    ],
    transformationTimeline: '6-12 months',
    investmentRange: { low: 200000, high: 2000000 },
    expectedROI: '200-350%',
    priorityUseCases: [
      'Personalized Recommendations',
      'Dynamic Pricing Optimization',
      'Demand Forecasting',
      'Inventory Optimization',
      'Customer Segmentation'
    ]
  },
  
  manufacturing: {
    industryName: 'Manufacturing',
    transformationFocus: ['Production Optimization', 'Quality Control', 'Supply Chain', 'Asset Management'],
    digitalMaturityBaseline: 2.8,
    targetMaturity: 4.4,
    valueMultipliers: {
      revenue: 0.12,
      cost: 0.20,
      margin: 0.04,
      asset: 0.30,
      productivity: 0.38,
      throughput: 0.35,
      workingCapital: 0.18
    },
    keyKPIs: [
      'Overall Equipment Effectiveness',
      'First Pass Yield',
      'Production Cycle Time',
      'Energy Consumption',
      'Maintenance Cost'
    ],
    transformationTimeline: '12-24 months',
    investmentRange: { low: 500000, high: 5000000 },
    expectedROI: '250-400%',
    priorityUseCases: [
      'Predictive Maintenance',
      'Quality Inspection',
      'Production Scheduling',
      'Energy Optimization',
      'Supply Chain Visibility'
    ]
  },
  
  autonomous: {
    industryName: 'Autonomous & Robotics',
    transformationFocus: ['Safety & Reliability', 'Autonomous Operations', 'Real-time Decision Making', 'Fleet Management'],
    digitalMaturityBaseline: 3.5,
    targetMaturity: 4.9,
    valueMultipliers: {
      revenue: 0.25,
      cost: 0.15,
      margin: 0.06,
      asset: 0.35,
      productivity: 0.45,
      throughput: 0.40,
      workingCapital: 0.10
    },
    keyKPIs: [
      'Safety Incident Rate',
      'Autonomous Operation Hours',
      'Fleet Utilization',
      'Mission Success Rate',
      'Response Time'
    ],
    transformationTimeline: '24-36 months',
    investmentRange: { low: 2000000, high: 20000000 },
    expectedROI: '300-500%',
    priorityUseCases: [
      'Autonomous Navigation',
      'Predictive Maintenance',
      'Fleet Optimization',
      'Real-time Decision Making',
      'Safety Monitoring'
    ]
  },
  
  customerService: {
    industryName: 'Customer Service',
    transformationFocus: ['Customer Experience', 'Service Automation', 'Agent Productivity', 'Quality Assurance'],
    digitalMaturityBaseline: 2.5,
    targetMaturity: 4.2,
    valueMultipliers: {
      revenue: 0.10,
      cost: 0.28,
      margin: 0.03,
      asset: 0.12,
      productivity: 0.35,
      throughput: 0.25,
      workingCapital: 0.08
    },
    keyKPIs: [
      'Customer Satisfaction',
      'First Contact Resolution',
      'Average Handle Time',
      'Agent Utilization',
      'Customer Effort Score'
    ],
    transformationTimeline: '3-6 months',
    investmentRange: { low: 100000, high: 1000000 },
    expectedROI: '200-300%',
    priorityUseCases: [
      'AI Chatbots',
      'Sentiment Analysis',
      'Ticket Routing',
      'Response Automation',
      'Quality Monitoring'
    ]
  },
  
  general: {
    industryName: 'General Enterprise',
    transformationFocus: ['Operational Excellence', 'Customer Centricity', 'Innovation', 'Governance'],
    digitalMaturityBaseline: 2.0,
    targetMaturity: 4.0,
    valueMultipliers: {
      revenue: 0.15,
      cost: 0.20,
      margin: 0.03,
      asset: 0.20,
      productivity: 0.30,
      throughput: 0.25,
      workingCapital: 0.15
    },
    keyKPIs: [
      'Operational Efficiency',
      'Customer Satisfaction',
      'Time to Market',
      'Cost Reduction',
      'Innovation Rate'
    ],
    transformationTimeline: '12-18 months',
    investmentRange: { low: 200000, high: 2000000 },
    expectedROI: '200-300%',
    priorityUseCases: [
      'Process Automation',
      'Decision Support',
      'Customer Insights',
      'Risk Management',
      'Resource Optimization'
    ]
  }
};

export const MATURITY_DIMENSIONS = [
  {
    id: 'strategy',
    name: 'Strategy & Leadership',
    levels: [
      { level: 1, name: 'Ad-hoc', description: 'No formal AI strategy' },
      { level: 2, name: 'Defined', description: 'Basic AI awareness and planning' },
      { level: 3, name: 'Managed', description: 'Formal AI strategy with governance' },
      { level: 4, name: 'Integrated', description: 'AI embedded in business strategy' },
      { level: 5, name: 'Optimized', description: 'AI-driven business transformation' }
    ]
  },
  {
    id: 'data',
    name: 'Data & Infrastructure',
    levels: [
      { level: 1, name: 'Siloed', description: 'Data scattered, no governance' },
      { level: 2, name: 'Consolidated', description: 'Central data repository' },
      { level: 3, name: 'Governed', description: 'Data quality and governance in place' },
      { level: 4, name: 'Optimized', description: 'Real-time data pipelines' },
      { level: 5, name: 'Intelligent', description: 'Self-service data platform' }
    ]
  },
  {
    id: 'talent',
    name: 'Talent & Skills',
    levels: [
      { level: 1, name: 'Limited', description: 'No dedicated AI team' },
      { level: 2, name: 'Emerging', description: 'Small data science team' },
      { level: 3, name: 'Capable', description: 'Dedicated AI team with skills' },
      { level: 4, name: 'Advanced', description: 'CoE with specialized roles' },
      { level: 5, name: 'Leading', description: 'World-class AI talent' }
    ]
  },
  {
    id: 'process',
    name: 'Process & Governance',
    levels: [
      { level: 1, name: 'None', description: 'No formal processes' },
      { level: 2, name: 'Basic', description: 'Ad-hoc processes' },
      { level: 3, name: 'Standardized', description: 'Documented processes' },
      { level: 4, name: 'Automated', description: 'MLOps and CI/CD' },
      { level: 5, name: 'Optimized', description: 'Continuous improvement' }
    ]
  },
  {
    id: 'technology',
    name: 'Technology & Tools',
    levels: [
      { level: 1, name: 'Basic', description: 'Manual tools' },
      { level: 2, name: 'Standard', description: 'Basic ML tools' },
      { level: 3, name: 'Advanced', description: 'Enterprise ML platform' },
      { level: 4, name: 'Integrated', description: 'End-to-end AI platform' },
      { level: 5, name: 'Leading-edge', description: 'Cutting-edge technology' }
    ]
  }
];

export const VALUE_ENGINEERING_BENCHMARKS = {
  ebitdaImpact: {
    byIndustry: {
      healthcare: { revenueUplift: 0.15, costReduction: 0.22, marginImprovement: 0.04 },
      finance: { revenueUplift: 0.12, costReduction: 0.25, marginImprovement: 0.05 },
      ecommerce: { revenueUplift: 0.20, costReduction: 0.18, marginImprovement: 0.03 },
      manufacturing: { revenueUplift: 0.10, costReduction: 0.20, marginImprovement: 0.04 },
      autonomous: { revenueUplift: 0.25, costReduction: 0.15, marginImprovement: 0.06 },
      customerService: { revenueUplift: 0.08, costReduction: 0.28, marginImprovement: 0.03 },
      general: { revenueUplift: 0.12, costReduction: 0.20, marginImprovement: 0.03 }
    }
  },
  assetUtilization: {
    benchmarks: {
      startup: { current: 0.45, target: 0.65, improvement: 0.20 },
      smb: { current: 0.50, target: 0.75, improvement: 0.25 },
      enterprise: { current: 0.55, target: 0.85, improvement: 0.30 }
    }
  },
  engineeringProductivity: {
    benchmarks: {
      startup: { current: 0.50, target: 0.80, improvement: 0.30 },
      smb: { current: 0.55, target: 0.85, improvement: 0.30 },
      enterprise: { current: 0.60, target: 0.90, improvement: 0.30 }
    }
  },
  manufacturingThroughput: {
    benchmarks: {
      startup: { current: 0.50, target: 0.75, improvement: 0.25 },
      smb: { current: 0.55, target: 0.80, improvement: 0.25 },
      enterprise: { current: 0.60, target: 0.85, improvement: 0.25 }
    }
  },
  workingCapital: {
    benchmarks: {
      startup: { reduction: 0.15, cashRelease: 50000 },
      smb: { reduction: 0.20, cashRelease: 250000 },
      enterprise: { reduction: 0.25, cashRelease: 2500000 }
    }
  }
};

export const TRANSFORMATION_KPIS = {
  financial: [
    { kpi: 'EBITDA Margin', baseline: '15%', target: '20%', improvement: '+5%' },
    { kpi: 'Revenue Growth', baseline: '8%', target: '15%', improvement: '+7%' },
    { kpi: 'Cost Reduction', baseline: '0%', target: '20%', improvement: '-20%' },
    { kpi: 'ROI', baseline: '0%', target: '300%', improvement: '+300%' }
  ],
  operational: [
    { kpi: 'Process Efficiency', baseline: '60%', target: '85%', improvement: '+25%' },
    { kpi: 'Automation Rate', baseline: '20%', target: '60%', improvement: '+40%' },
    { kpi: 'Cycle Time', baseline: '100%', target: '60%', improvement: '-40%' },
    { kpi: 'Error Rate', baseline: '5%', target: '0.5%', improvement: '-90%' }
  ],
  customer: [
    { kpi: 'CSAT', baseline: '75%', target: '90%', improvement: '+15%' },
    { kpi: 'NPS', baseline: '30', target: '50', improvement: '+20' },
    { kpi: 'Retention', baseline: '80%', target: '95%', improvement: '+15%' },
    { kpi: 'Response Time', baseline: '24hrs', target: '1hr', improvement: '-95%' }
  ],
  innovation: [
    { kpi: 'Use Cases Deployed', baseline: '0', target: '10+', improvement: '+10' },
    { kpi: 'Time to Deploy', baseline: '3 months', target: '1 week', improvement: '-90%' },
    { kpi: 'Model Accuracy', baseline: '80%', target: '95%', improvement: '+15%' }
  ]
};